/**
 * DNKADM-0475 (F-99): the copy and link for a `409 ORDER_NEEDS_ATTENTION` (DNKAPI-0690), the
 * refusal a card sale gets while an earlier card order of the same member is still open.
 * Rendered by {@link OrderNeedsAttentionAlert} in the sell cart, buy membership and walk-in
 * dialogs.
 */
import { parseOrderNeedsAttention, type OpenOrderNeedsAttention } from '@/api/membershipOrders';
import { isNeedsReview } from '@/domain/orderStatus';

/** The staff-facing sentence for an open order the server named. */
export function orderNeedsAttentionMessage(openOrder: OpenOrderNeedsAttention): string {
  const ref = openOrder.orderNumber ?? (openOrder.orderId === null ? null : String(openOrder.orderId));
  if (ref === null) {
    return 'This member has an open order that needs attention. This sale was not charged. Check Payments before charging again.';
  }
  if (isNeedsReview(openOrder.status)) {
    return `This member has an open order ${ref}: a card was charged but nothing was applied yet. This sale was not charged. Check Payments before charging again.`;
  }
  return `This member has an open order ${ref} that needs attention. This sale was not charged. Check Payments before charging again.`;
}

/** The member's Payments in the transaction ledger, narrowed to the member when known. */
export function memberPaymentsPath(contactId: string | null): string {
  return contactId === null
    ? '/transaction-ledger?tab=payments'
    : `/transaction-ledger?tab=payments&contactId=${encodeURIComponent(contactId)}`;
}

/**
 * A dialog's error view, replaced by the open-order view when `error` is the
 * `ORDER_NEEDS_ATTENTION` refusal. That refusal wrote nothing, so it is never retryable here:
 * the way forward is Payments.
 */
export function withOpenOrder<T extends { message: string; retryable: boolean }>(
  error: unknown,
  view: T,
): T & { openOrder?: OpenOrderNeedsAttention } {
  const openOrder = parseOrderNeedsAttention(error);
  if (openOrder === null) return view;
  return { ...view, retryable: false, message: orderNeedsAttentionMessage(openOrder), openOrder };
}
